import { Formik } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "../../api/axios";
import { Password, Select, Text } from "../Forms";
import { ModalSaveButton } from "../Modals";
import { useNavigate } from "react-router-dom";
import { setSessionData } from "../../actions/session";
import { useDispatch } from "react-redux";

const CUSTOM_QUESTION = "Custom question";

const rowMargin = { marginTop: 24 };

export default function SecurityQuestions() {
  const [hasASecurityQuestion, setHasASecurityQuestion] = useState(undefined);
  const [questions, setQuestions] = useState([]);
  const [buttonType, setButtonType] = useState("submit");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    const getPastQuestions = async () => {
      try {
        const generalQuestionsResponse = await axios.get(
          "api/securityquestions"
        );
        const usersQuestionsReponse = await axios.get("api/user_recovery");
        const savedQuestions = usersQuestionsReponse.data.data.map(
          (question) => question.question
        );
        const questionOptions = generalQuestionsResponse.data
          .map((question) => ({
            value: question.question,
            label: question.question,
          }))
          .filter((question) => !savedQuestions.includes(question.value));
        setQuestions([
          {
            value: "Please select a security question...",
            label: "Please select a security question...",
            isDisabled: true,
          },
          ...questionOptions,
          {
            value: "separator",
            label: "",
            isDisabled: true,
            role: "separator",
          },
          { value: CUSTOM_QUESTION, label: CUSTOM_QUESTION },
        ]);
        setHasASecurityQuestion(usersQuestionsReponse.data.data?.[0] || false);
      } catch (error) {
        //
      }
    };
    getPastQuestions();
  }, []);

  const onSubmit = useCallback(
    async (values, { resetForm, setErrors }) => {
      const obj = {
        questions: [
          {
            question:
              values.question === CUSTOM_QUESTION
                ? values.secret_custom
                : values.question,
            answer: values.secret_answer,
          },
        ],
        password: values.password,
      };
      try {
        await axios.post("api/user_recovery", obj);
        const res = await axios.get("api/profile");
        setButtonType("success");
        dispatch(setSessionData(res.data.data));
        navigate("/settings");
      } catch (error) {
        setErrors(error?.errors || {});
        setButtonType("error");
      }
    },
    [dispatch, navigate]
  );

  const initialValues = useMemo(
    () => ({
      question: "",
      secret_custom: "",
      secret_answer: "",
      password: "",
    }),
    []
  );

  if (hasASecurityQuestion === undefined) {
    return null;
  }

  return (
    <div>
      <h1 className="white">
        {hasASecurityQuestion ? "Change your" : "Set a"} security question
      </h1>
      <div className="program__wrap donation-form__content">
        <div className="panel">
          <div className="form_section donation_panel">
            <div className="form donation_panel__content">
              {hasASecurityQuestion ? (
                <>
                  <p>
                    For certain things, like changing your password or personal
                    details, we'll want to be extra sure it's you.
                  </p>
                  <p>
                    Please set a security question and answer below. Make sure
                    the answer is memorable to you, and is not easily guessable.
                  </p>
                </>
              ) : (
                <>
                  <p>
                    We're improving our security for certain things, like
                    resetting your password or changing your personal details,
                    to be extra sure it's you.
                  </p>
                  <p>
                    As you don't have one yet, please set a security question
                    and answer below. Make sure the answer is memorable to you,
                    and is not easily guessable.
                  </p>
                </>
              )}
            </div>
            <Formik onSubmit={onSubmit} initialValues={initialValues}>
              {({ handleSubmit, values, isSubmitting }) => (
                <form onSubmit={handleSubmit}>
                  <div className="form donation_panel__content">
                    <div className="row" style={rowMargin}>
                      <div className="form__block">
                        <Select
                          label="Security question"
                          name="question"
                          options={questions}
                        />
                        {values.question === "Custom question" && (
                          <Text
                            name="secret_custom"
                            placeholder="Write your question"
                          />
                        )}
                        <div style={rowMargin}>
                          <Text
                            label="Answer"
                            name="secret_answer"
                            placeholder="Answer"
                            autoComplete="none"
                          />
                        </div>
                        <div className="form_row row" style={rowMargin}>
                          <div className="small-12 columns">
                            <h2>Enter your password</h2>
                            <p>
                              In order to better protect your account, we
                              require you to enter your password when{" "}
                              {hasASecurityQuestion ? "changing" : "setting"}{" "}
                              your security questions.
                            </p>
                            <Password
                              label="Current password"
                              name="password"
                              autoComplete="current-password"
                            />
                          </div>
                        </div>
                        <div
                          className="flex-row form__finalise-changes"
                          id="login-submit"
                          style={{ ...rowMargin, gap: 20 }}
                        >
                          {hasASecurityQuestion && (
                            <a
                              href="/settings"
                              className="btn_sec tooltip remembered_password__link"
                              original-title="Don't want to change your security question? Click here to return."
                            >
                              Cancel
                            </a>
                          )}
                          <ModalSaveButton
                            variant={isSubmitting ? "saving" : buttonType}
                          >
                            {hasASecurityQuestion ? "Save" : "Reset"}
                          </ModalSaveButton>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
}
